import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Calendar, MapPin, Users, Play, ArrowLeft, Trophy } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GameSetup = ({ defaultDate, onSessionCreated, onBack }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sports, setSports] = useState([]);
  const [positions, setPositions] = useState([]);
  const [formData, setFormData] = useState({
    game_date: defaultDate || new Date().toISOString().split('T')[0],
    opponent_name: '',
    sport: user?.focused_sport || '',
    position: '',
    notes: '',
    is_home_game: true
  });

  useEffect(() => {
    fetchSportsAndPositions();
  }, []);

  // When sport changes, fetch positions for that sport
  useEffect(() => {
    if (formData.sport) {
      fetchPositionsForSport(formData.sport);
      // Reset position when sport changes
      setFormData(prev => ({ ...prev, position: '' }));
    }
  }, [formData.sport]);

  const fetchSportsAndPositions = async () => {
    try {
      const response = await axios.get(`${API}/game-stats/sports`);
      setSports(response.data.sports || []);
      
      // If user has a focused sport, pre-select it and fetch positions
      if (user?.focused_sport) {
        fetchPositionsForSport(user.focused_sport);
      }
    } catch (error) {
      // Fallback sports
      setSports([
        { value: 'football', label: 'Football', icon: '🏈' },
        { value: 'soccer', label: 'Soccer', icon: '⚽' },
        { value: 'basketball', label: 'Basketball', icon: '🏀' },
        { value: 'baseball', label: 'Baseball', icon: '⚾' },
        { value: 'volleyball', label: 'Volleyball', icon: '🏐' },
      ]);
    }
  };

  const fetchPositionsForSport = async (sport) => {
    try {
      const response = await axios.get(`${API}/game-stats/positions?sport=${sport}`);
      setPositions(response.data.positions || []);
    } catch (error) {
      setPositions([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sport) {
      toast.error('Please select your sport');
      return;
    }
    
    if (!formData.opponent_name.trim()) {
      toast.error('Please enter the opponent name');
      return;
    }
    
    if (!formData.position) {
      toast.error('Please select your position');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/game-sessions`, {
        game_date: formData.game_date,
        opponent_name: formData.opponent_name,
        position: formData.position,
        notes: formData.notes,
        is_home_game: formData.is_home_game,
        sport: formData.sport
      });
      toast.success('Game session created! Ready to track stats.');
      onSessionCreated(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create game session');
    } finally {
      setLoading(false);
    }
  };

  const formatPosition = (pos) => {
    return pos.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-lg mx-auto">
        <Button
          variant="ghost"
          className="text-slate-400 hover:text-white mb-4"
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="bg-slate-800/90 border-slate-700 backdrop-blur-xl">
          <CardHeader>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-2">
              <Play className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-xl font-bold text-white">
              Start Video Review
            </CardTitle>
            <CardDescription className="text-slate-300">
              Set up your game session to begin tracking stats
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Sport Selection */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Sport
                </Label>
                <Select
                  value={formData.sport}
                  onValueChange={(value) => setFormData({...formData, sport: value})}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="sport-select">
                    <SelectValue placeholder="Select your sport" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600 max-h-[300px]">
                    {sports.map((sport) => (
                      <SelectItem key={sport.value} value={sport.value} className="text-white hover:bg-slate-600">
                        <span className="flex items-center gap-2">
                          <span>{sport.icon}</span>
                          <span>{sport.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Game Date */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Game Date
                </Label>
                <Input
                  type="date"
                  value={formData.game_date}
                  onChange={(e) => setFormData({...formData, game_date: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="game-date-input"
                />
              </div>

              {/* Opponent Name */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Opponent Name
                </Label>
                <Input
                  type="text"
                  placeholder="e.g., Riverside High School"
                  value={formData.opponent_name}
                  onChange={(e) => setFormData({...formData, opponent_name: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="opponent-name-input"
                />
              </div>

              {/* Position - only show if sport is selected */}
              {formData.sport && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Your Position</Label>
                  <Select
                    value={formData.position}
                    onValueChange={(value) => setFormData({...formData, position: value})}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="position-select">
                      <SelectValue placeholder="Select your position" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {positions.map((pos) => (
                        <SelectItem key={pos} value={pos} className="text-white hover:bg-slate-600">
                          {formatPosition(pos)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Home/Away Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <Label className="text-slate-300">Home Game</Label>
                </div>
                <Switch
                  checked={formData.is_home_game}
                  onCheckedChange={(checked) => setFormData({...formData, is_home_game: checked})}
                  data-testid="home-game-switch"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-slate-300">Notes (Optional)</Label>
                <Textarea
                  placeholder="Any notes about this game or session..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 min-h-[80px]"
                  data-testid="game-notes-input"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-6"
                disabled={loading || !formData.sport}
                data-testid="start-tracking-btn"
              >
                {loading ? 'Creating Session...' : 'Start Tracking Stats'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GameSetup;
